import React, { useState, useEffect, useCallback, memo, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { htmlToMarkdown } from '../../utils/htmlToMarkdown';

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'code', 'pre', 'blockquote', 'span'];

// Tags that should be completely removed including their content
const REMOVE_ENTIRELY = ['style', 'script', 'head', 'meta', 'link', 'title', 'noscript', 'svg', 'iframe', 'object', 'embed'];

const sanitizeNode = (node) => {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (REMOVE_ENTIRELY.includes(tag)) {
        // Remove entirely — don't keep text content (e.g. CSS rules from <style>)
        node.removeChild(child);
      } else if (!ALLOWED_TAGS.includes(tag)) {
        // Replace unsupported tags with their text content
        const text = document.createTextNode(child.textContent);
        node.replaceChild(text, child);
      } else {
        // Strip all attributes except href on links
        const attrs = Array.from(child.attributes);
        for (const attr of attrs) {
          if (!(tag === 'a' && attr.name === 'href')) {
            child.removeAttribute(attr.name);
          }
        }
        sanitizeNode(child);
      }
    }
  }
};

const sanitizeHtml = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  sanitizeNode(temp);
  return temp.innerHTML;
};

/**
 * IsolatedChatInput - A rich text chat input component with isolated local state
 *
 * Uses contentEditable to preserve formatting (bold, bullets, etc.) when pasting
 * rich text. Converts HTML to markdown on submit/getValue for ReactMarkdown rendering.
 *
 * This component manages typing state locally to prevent parent re-renders
 * during typing. It syncs with parent state when:
 * 1. Parent's externalValue changes (e.g., speech recognition, quick create)
 * 2. User submits (Enter key)
 * 3. Component exposes methods via ref for parent to get/set value
 */
const IsolatedChatInput = memo(forwardRef(({
  externalValue = '',
  onSubmit,
  onValueChange,
  placeholder,
  disabled = false,
  minRows = 1,
  maxRows = 18,
  autoExpand = true,
  InputProps = {},
  sx = {},
  ...restProps
}, ref) => {
  const editableRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const lastExternalValue = useRef(externalValue);

  // Extract endAdornment from InputProps
  const endAdornment = InputProps?.endAdornment || null;

  const checkEmpty = useCallback(() => {
    if (editableRef.current) {
      const text = editableRef.current.textContent?.trim();
      setIsEmpty(!text);
    }
  }, []);

  // Auto-resize based on content
  const autoResize = useCallback(() => {
    const el = editableRef.current;
    if (!el || !autoExpand) return;

    el.style.height = 'auto';
    const computedStyle = window.getComputedStyle(el);
    const lineHeight = parseInt(computedStyle.lineHeight) || 25;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;

    const minHeight = (lineHeight * minRows) + paddingTop + paddingBottom;
    const maxHeight = (lineHeight * maxRows) + paddingTop + paddingBottom;
    const newHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoExpand, minRows, maxRows]);

  // Convert plain text with \n to HTML with <br> for contentEditable display
  const textToHtml = useCallback((text) => {
    if (!text) return '';
    // Escape HTML entities, then convert newlines to <br>
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped.replace(/\n/g, '<br>');
  }, []);

  // Sync from parent when externalValue changes (speech recognition, programmatic updates)
  useEffect(() => {
    if (externalValue !== lastExternalValue.current && editableRef.current) {
      lastExternalValue.current = externalValue;
      if (externalValue) {
        editableRef.current.innerHTML = textToHtml(externalValue);
      } else {
        editableRef.current.innerHTML = '';
      }
      checkEmpty();
      autoResize();
    }
  }, [externalValue, checkEmpty, autoResize, textToHtml]);

  // Handle input changes
  const handleInput = useCallback(() => {
    checkEmpty();
    autoResize();
  }, [checkEmpty, autoResize]);

  // Helper to insert HTML fragment at current cursor position
  const insertAtCursor = useCallback((fragment) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(fragment);

    // Move cursor to end of inserted content
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Convert plain text with \n to HTML fragment with <br> tags
  const textToFragment = useCallback((text) => {
    const fragment = document.createDocumentFragment();
    const lines = text.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) {
        fragment.appendChild(document.createElement('br'));
      }
      if (line) {
        fragment.appendChild(document.createTextNode(line));
      }
    });
    return fragment;
  }, []);

  // Handle paste - preserve rich text formatting and line breaks
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    const html = clipboardData.getData('text/html');
    const text = clipboardData.getData('text/plain');

    if (html) {
      const sanitized = sanitizeHtml(html);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitized;
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      insertAtCursor(fragment);
    } else if (text) {
      // Convert \n to <br> so line breaks render in contentEditable
      insertAtCursor(textToFragment(text));
    }

    checkEmpty();
    autoResize();
  }, [checkEmpty, autoResize, insertAtCursor, textToFragment]);

  // Handle Enter key to submit, Shift+Enter for line break
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (onSubmit && editableRef.current) {
        const markdown = htmlToMarkdown(editableRef.current.innerHTML);
        onSubmit(e, markdown);
        editableRef.current.innerHTML = '';
        checkEmpty();
        if (editableRef.current) {
          editableRef.current.style.height = 'auto';
        }
      }
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Insert <br> consistently across browsers for Shift+Enter line break
      e.preventDefault();
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();
      const br = document.createElement('br');
      range.insertNode(br);

      // Add a trailing <br> if at end of content so cursor moves to new line
      if (!br.nextSibling || (br.nextSibling.nodeType === Node.ELEMENT_NODE && br.nextSibling.tagName === 'BR')) {
        const extraBr = document.createElement('br');
        br.parentNode.insertBefore(extraBr, br.nextSibling);
      }

      // Move cursor after the inserted <br>
      range.setStartAfter(br);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      autoResize();
    }
  }, [onSubmit, checkEmpty, autoResize]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (editableRef.current) {
        return htmlToMarkdown(editableRef.current.innerHTML);
      }
      return '';
    },
    setValue: (value) => {
      if (editableRef.current) {
        editableRef.current.innerHTML = textToHtml(value);
        lastExternalValue.current = value;
        checkEmpty();
        autoResize();
      }
    },
    clear: () => {
      if (editableRef.current) {
        editableRef.current.innerHTML = '';
        editableRef.current.style.height = 'auto';
        lastExternalValue.current = '';
        checkEmpty();
      }
    },
    focus: () => {
      if (editableRef.current) {
        editableRef.current.focus();
      }
    }
  }), [checkEmpty, autoResize, textToHtml]);

  return (
    <Box sx={{ width: '100%', position: 'relative', ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          borderRadius: '24px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          border: `1px solid ${focused ? '#485869' : 'rgba(0, 0, 0, 0.1)'}`,
          '&:hover': {
            borderColor: '#485869',
          },
          transition: 'border-color 0.2s',
          paddingRight: '8px',
          position: 'relative',
        }}
      >
        <Box sx={{ flex: 1, position: 'relative', minHeight: '40px' }}>
          {/* Placeholder */}
          {isEmpty && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: '0.5rem 1rem',
                color: 'text.disabled',
                fontSize: '15px',
                // lineHeight: '40px',
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {placeholder}
            </Box>
          )}

          {/* ContentEditable input */}
          <Box
            ref={editableRef}
            contentEditable={!disabled}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            suppressContentEditableWarning
            sx={{
              padding: '0.5rem 1rem',
              fontSize: '15px',
              lineHeight: '25px',
              minHeight: '40px',
              maxHeight: '600px',
              overflow: 'auto',
              resize: 'vertical',
              outline: 'none',
              wordBreak: 'break-word',
              // Rich text styles for pasted content
              '& b, & strong': { fontWeight: 'bold' },
              '& i, & em': { fontStyle: 'italic' },
              '& u': { textDecoration: 'underline' },
              '& ul': { paddingLeft: '20px', margin: '4px 0', listStyleType: 'disc' },
              '& ol': { paddingLeft: '20px', margin: '4px 0', listStyleType: 'decimal' },
              '& li': { marginBottom: '2px' },
              '& h1': { fontSize: '1.5em', fontWeight: 'bold', margin: '4px 0' },
              '& h2': { fontSize: '1.3em', fontWeight: 'bold', margin: '4px 0' },
              '& h3': { fontSize: '1.15em', fontWeight: 'bold', margin: '4px 0' },
              '& h4, & h5, & h6': { fontWeight: 'bold', margin: '4px 0' },
              '& a': { color: 'primary.main', textDecoration: 'underline' },
              '& code': { backgroundColor: 'rgba(0, 0, 0, 0.06)', padding: '1px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '0.9em' },
              '& pre': { backgroundColor: 'rgba(0, 0, 0, 0.06)', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9em', overflow: 'auto' },
              '& blockquote': { borderLeft: '3px solid rgba(0, 0, 0, 0.2)', paddingLeft: '12px', margin: '4px 0', color: 'text.secondary' },
              // Scrollbar styles
              '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-track': { borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.05)', marginTop: '5px', marginBottom: '5px' },
              '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.2)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.4)' } },
              '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
            }}
          />
        </Box>

        {/* End adornment (stop/send buttons) */}
        {endAdornment && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, pb: '4px' }}>
            {endAdornment}
          </Box>
        )}
      </Box>
    </Box>
  );
}));

IsolatedChatInput.displayName = 'IsolatedChatInput';

IsolatedChatInput.propTypes = {
  externalValue: PropTypes.string,
  onSubmit: PropTypes.func,
  onValueChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  autoExpand: PropTypes.bool,
  InputProps: PropTypes.object,
  sx: PropTypes.object,
};

export default IsolatedChatInput;
